import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { FormantiIcon } from "@/components/FormantiLogo";
import SportIcon from "@/components/kit/SportIcon";

/**
 * The footer, on every public page: the policy links the payment providers and the stores look for, the sports, and the contact address.
 * `compact` is the one-row version for the app pages (dashboard, analyze, wallet...); the full one is for the marketing pages. Below md it
 * keeps clear of the fixed bottom navigation. Not shown on /auth, /admin, the test pages or the live camera screens (RouteFooter below, and
 * the data-camera flag on the html element, see index.css).
 */
const SPORT_LINKS = [
  ["/badminton", "Badminton Coach", "badminton"], ["/tennis", "Tennis Coach", "tennis"], ["/table-tennis", "Table Tennis Coach", "table_tennis"],
  ["/pickleball", "Pickleball Coach", "pickleball"], ["/basketball", "Basketball Coach", "basketball"], ["/cricket", "Cricket Coach", "cricket"],
  ["/football", "Football Coach", "football"], ["/swimming", "Swimming Coach", "swimming"], ["/gym", "Gym", "gym"],
  ["/weight-lifting", "Weight Lifting", "lifting"], ["/physiotherapy", "Physiotherapy", "gym"],
];

export const FOOTER_COLS = [
  { title: "Product", links: [["/auth", "Video Analysis"], ["/practice", "Live Practice"], ["/auth", "Training Plans"], ["/auth", "Equipment Finder"], ["/pricing", "Pricing"], ["/download", "Install the App"], ["/community", "Community Games"]] },
  { title: "Sports", links: SPORT_LINKS },
  { title: "Resources", links: [["/blog", "Blog"], ["/auth", "Sign Up"], ["/auth", "Login"], ["/community", "Community"], ["/privacy", "Privacy Policy"], ["/terms", "Terms & Conditions"], ["/refund", "Refund Policy"], ["/cancellation", "Cancellation Policy"], ["/contact", "Contact Us"]] },
];

const LEGAL = [["/privacy", "Privacy"], ["/terms", "Terms"], ["/refund", "Refunds"], ["/cancellation", "Cancellation"], ["/shipping", "Shipping"], ["/contact", "Contact"]];

/** Routes that never get the shared footer: sign-in, admin, the test and dev pages, the labelling tool, and "/" (which draws its own). */
export const NO_FOOTER = (path) => path === "/" || path === "/auth" || path === "/admin" || path === "/label" || path.startsWith("/test-") || path.startsWith("/dev/");
/** The app's own pages get the one-row footer. */
const APP_PAGES = ["/dashboard", "/analyze", "/wallet", "/progress", "/profile", "/training", "/community", "/card", "/assessment", "/referral"];
const isAppPage = (path) => APP_PAGES.some((p) => path === p || path.startsWith(p + "/"));

const linkIcon = (icon) => (icon ? <SportIcon sport={icon} size={16} className="shrink-0 opacity-80" /> : null);

function Legal({ className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`}>
      <p className="text-zinc-500 text-xs">&copy; {new Date().getFullYear()} Formanti. All rights reserved.</p>
      {LEGAL.map(([to, label]) => <Link key={to} to={to} className="text-zinc-400 hover:text-zinc-200 text-xs transition-colors py-2 inline-block">{label}</Link>)}
      <a href="mailto:info@formanti.com" className="text-zinc-400 hover:text-zinc-200 text-xs transition-colors py-2 inline-block">info@formanti.com</a>
      {/* attribution the CC BY licences ask for: the illustrated films' motion and the music */}
      <p className="basis-full text-zinc-600 text-[11px] lg:text-xs leading-snug" data-testid="film-credits">
        Illustrated films: motion traced from videos by Prashanth et al. (<a href="https://doi.org/10.17632/kgbb3yn47p.2" target="_blank" rel="noopener noreferrer" className="underline">Mendeley Data, CC BY 4.0</a>). Music: <a href="https://ende.app/en" target="_blank" rel="noopener noreferrer" className="underline">Sascha Ende</a>, CC BY 4.0.
      </p>
    </div>
  );
}

export default function SiteFooter({ compact = false }) {
  if (compact) {
    return (
      <footer className="site-footer border-t border-white/5 bg-zinc-950 pt-5 pb-[calc(84px+env(safe-area-inset-bottom))] md:pb-6" data-testid="site-footer">
        <div className="container mx-auto px-4 max-w-6xl"><Legal /></div>
      </footer>
    );
  }
  return (
    <footer className="site-footer py-10 md:py-16 border-t border-zinc-800/50 bg-zinc-950 pb-[calc(96px+env(safe-area-inset-bottom))] md:pb-16" data-testid="site-footer">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Phones: one tap-to-open row per group (the open footer was a screen and a half). */}
        <div className="md:hidden mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FormantiIcon className="h-5" />
            <span className="font-heading font-bold text-lg text-white">Formanti</span>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed mb-4">
            AI-powered sports coaching platform. Train smarter across every sport you love.
          </p>
          <p className="text-zinc-400 text-sm mb-4">Questions? Email <a href="mailto:info@formanti.com" className="text-lime-400 hover:text-lime-300 transition-colors">info@formanti.com</a></p>
          {FOOTER_COLS.map((c) => (
            <details key={c.title} className="group border-t border-zinc-800/70 last:border-b">
              <summary className="flex items-center justify-between min-h-12 py-3 cursor-pointer list-none text-white font-semibold text-sm">
                {c.title}
                <ChevronDown className="w-4 h-4 text-lime-400 transition-transform group-open:rotate-180 lg:w-5 lg:h-5" aria-hidden="true" />
              </summary>
              <ul className="pb-3 grid grid-cols-2 gap-x-4">
                {c.links.map(([to, label, icon]) => (
                  <li key={label}><Link to={to} className="text-zinc-300 hover:text-lime-400 text-sm transition-colors inline-flex items-center gap-2 min-h-11">{linkIcon(icon)}{label}</Link></li>
                ))}
              </ul>
            </details>
          ))}
        </div>

        {/* md and up: four columns. */}
        <div className="hidden md:grid md:grid-cols-4 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <FormantiIcon className="h-5" />
              <span className="font-heading font-bold text-lg text-white">Formanti</span>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              AI-powered sports coaching platform. Train smarter across every sport you love.
            </p>
            <p className="text-zinc-400 text-sm mt-4">Questions? Email<br /><a href="mailto:info@formanti.com" className="text-lime-400 hover:text-lime-300 transition-colors">info@formanti.com</a></p>
          </div>
          {FOOTER_COLS.map((c) => (
            <div key={c.title}>
              <h2 className="text-white font-semibold text-sm mb-4">{c.title}</h2>
              <ul className="space-y-1">
                {c.links.map(([to, label, icon]) => (
                  <li key={label}><Link to={to} className="text-zinc-400 hover:text-lime-400 text-sm transition-colors py-1 inline-flex items-center gap-2">{linkIcon(icon)}{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-zinc-800/50 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <Legal />
          <div className="flex flex-wrap items-center gap-3 text-zinc-500">
            {SPORT_LINKS.map(([to, label, icon]) => (
              <Link key={to} to={to} title={label} aria-label={label} className="hover:text-lime-300 transition-colors p-1"><SportIcon sport={icon} size={18} /></Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/** The App-level footer: picks the full or the compact one from the route, and none where it does not belong. */
export function RouteFooter() {
  const { pathname } = useLocation();
  if (NO_FOOTER(pathname)) return null;
  return <SiteFooter compact={isAppPage(pathname)} />;
}
